import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Users, Trash2, Battery, Signal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LiveMap from "@/components/LiveMap";
import PageHero from "@/components/PageHero";
import StoryPromptDialog from "@/components/StoryPromptDialog";

const SILENCE_MS = 3 * 60 * 60 * 1000; // 3 hours = "no contact" threshold

const RANDOM_CITIES = [
  [12.9716, 77.5946], [28.6139, 77.209], [19.076, 72.8777], [13.0827, 80.2707], [17.385, 78.4867],
];

export default function Family() {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", relation: "", phone: "", email: "" });
  const [storyOpen, setStoryOpen] = useState(false);
  const [storyContext, setStoryContext] = useState({ memberName: "", title: "" });

  const load = async () => {
    const { data } = await api.get("/family");
    setMembers(data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name || !form.relation) return toast.error("Name and relation required");
    try {
      const { data } = await api.post("/family", form);
      // Give them a starting location so map has data
      const [lat, lng] = RANDOM_CITIES[Math.floor(Math.random() * RANDOM_CITIES.length)];
      await api.post(`/family/${data.id}/location`, { latitude: lat + (Math.random() - 0.5) * 0.05, longitude: lng + (Math.random() - 0.5) * 0.05, battery: 60 + Math.floor(Math.random() * 40) });
      toast.success("Family member added");
      setForm({ name: "", relation: "", phone: "", email: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    await api.delete(`/family/${id}`);
    load();
  };

  const pingLocation = async (id) => {
    if (!navigator.geolocation) return toast.error("Location not available");
    navigator.geolocation.getCurrentPosition(async (p) => {
      // Check if this member was silent for too long — a fresh update becomes a "safe return" moment.
      const prev = members.find((m) => m.id === id);
      const wasSilent = !prev?.last_seen || (Date.now() - new Date(prev.last_seen).getTime() > SILENCE_MS);

      await api.post(`/family/${id}/location`, { latitude: p.coords.latitude, longitude: p.coords.longitude, battery: 100 });
      toast.success("Location updated");
      await load();

      if (wasSilent && prev?.name) {
        const promptedKey = `g360_family_reunion_${id}`;
        const lastPromptedDay = localStorage.getItem(promptedKey);
        const today = new Date().toISOString().slice(0, 10);
        if (lastPromptedDay !== today) {
          localStorage.setItem(promptedKey, today);
          setStoryContext({
            memberName: prev.name,
            title: `${prev.name} is safely back on the radar.`,
          });
          setStoryOpen(true);
        }
      }
    });
  };

  const points = members
    .filter((m) => m.last_lat && m.last_lng)
    .map((m) => ({ lat: m.last_lat, lng: m.last_lng, label: m.name, color: "secondary", radius: 300 }));

  return (
    <div className="max-w-6xl">
      <PageHero
        kicker="Family radar"
        title="Live safety map"
        subtitle="See your loved ones in real time."
        image="https://images.unsplash.com/photo-1640273296013-e4b54eaf52eb?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
        accent="secondary"
        testId="family-hero"
      />
      <div className="flex justify-end mb-4 flex-wrap gap-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-family-btn" className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Add family member
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add family member</DialogTitle>
              <DialogDescription>Add a loved one to your Family Radar. You can share location and receive alerts.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="family-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Relation</Label><Input data-testid="family-relation" value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value })} placeholder="Daughter, Spouse..." /></div>
              <div><Label>Phone</Label><Input data-testid="family-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input data-testid="family-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <Button data-testid="save-family-btn" onClick={add} className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">Add member</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-2xl overflow-hidden border border-border/70 mb-6" style={{ height: 400 }}>
        {points.length > 0 ? (
          <LiveMap points={points} showCircles zoom={12} />
        ) : (
          <div className="w-full h-full grid place-items-center bg-muted text-sm text-muted-foreground">
            Add family members to see them on the live safety map.
          </div>
        )}
      </div>

      {members.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-16 text-center">
          <Users className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-medium mb-1">No family members yet</div>
          <div className="text-sm text-muted-foreground">Invite loved ones to build your safety circle.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {members.map((m, i) => (
            <motion.div key={m.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="rounded-2xl border border-border/70 bg-card p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-display font-bold text-lg tracking-tight">{m.name}</div>
                  <div className="text-xs text-muted-foreground">{m.relation}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(m.id)} className="text-[hsl(var(--destructive))]" data-testid={`remove-family-${m.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              {m.last_seen ? (
                <div className="space-y-1.5 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5"><Signal className="w-3 h-3 text-[hsl(var(--secondary))]" /> Last seen: {new Date(m.last_seen).toLocaleTimeString()}</div>
                  <div className="flex items-center gap-1.5"><Battery className="w-3 h-3" /> Battery: {m.battery ?? "—"}%</div>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">No location yet.</div>
              )}
              <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => pingLocation(m.id)} data-testid={`ping-family-${m.id}`}>
                Update location
              </Button>
            </motion.div>
          ))}
        </div>
      )}

      <StoryPromptDialog
        open={storyOpen}
        onOpenChange={setStoryOpen}
        title={storyContext.title || "Great — everyone's safe."}
        description={`Take a moment to share how Guardian360 helped you stay in touch with ${storyContext.memberName || "your family"}. Approved stories may appear on our homepage.`}
      />
    </div>
  );
}
